import * as React from "react";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { createCopyOfTableContents } from "../../services/TableFunctions";
import PaginationView from "../../../repertoire/components/paginationView/PaginationView";
import DisplayTable, { IRowActions } from "../displayTable/DisplayTable";
import DisplayText from "../text/DisplayText";
import { IUnpaidClaimsSearchResult } from "../../types/IUnpaidClaimsSearchResult";
import { IDraftWorkSearchResult } from "../../types/IDraftWorkSearchResult";
import { IMemberWorkSearchResult } from "../../types/IMemberWorkSearchResult";
import { IMyPackagesResult } from "../../../repertoire/types/IMyPackagesResult";
import { showYesNoPrompt, hideModal } from "../../../redux/reducers/ModalReducer";
import { MODAL_VIEW, PAGINATION } from "../../../repertoire/Consts";
import { WORK_STATUS_LOOKUP } from "../../../lookup/Consts";
import { IPortalWorkStatusConfiguration } from "../../types/IPortalWorkStatusConfiguration";

interface ISearchResultsPageProps {
    searchSuccessful: boolean;
    searchResultsTableData: IRepertoireField[];
    searchResults: IUnpaidClaimsSearchResult[] | IMemberWorkSearchResult[];
    lookupValues?: ILookupDictionary;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    currentPage?: number;
    sortSearchResults?: (results: any) => void;
    resetPagination: (repertoireSection: string) => void;
    updatePagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    rowActions?: IRowActions;
    getAgreementDetails?: (dataSource: string, agreementID) => void;
    addToPackages?: (userName: string, entityType: string, entityIds: number[]) => IMyPackagesResult;
    showYesNoPrompt?: typeof showYesNoPrompt;
    hideModal?: typeof hideModal;
    repertoireSection?: string;
    packageActionsDisabled?: boolean;
    packageMessageData?: IRepertoireField[];
    portalWorkStatusConfiguration?: IPortalWorkStatusConfiguration;
}

const DisplayTableSearchResults: React.FC<ISearchResultsPageProps> = ({
    searchSuccessful,
    searchResultsTableData,
    searchResults,
    lookupValues,
    indexOfFirstResult,
    indexOfLastResult,
    resultsPerPage,
    currentPage,
    updatePagination,
    sortSearchResults,
    rowActions,
    getAgreementDetails,
    addToPackages,
    showYesNoPrompt,
    hideModal,
    repertoireSection,
    packageActionsDisabled,
    packageMessageData,
    portalWorkStatusConfiguration
}) => {
    const [currentTableData, setCurrentTableData] = React.useState([]);
    const [activeSortHeaderSection, setActiveHeaderSection] = React.useState('');
    const [isAscending, setIsAscending] = React.useState(false);


   

    const buildContributorSortKey = (contributors: any): string => {
        let arr: string[] = [];

        if (Array.isArray(contributors)) {
            arr = contributors.filter(c => c != null).map(c => String(c).trim());
        } else if (typeof contributors === 'string') {
            arr = [contributors.trim()];
        } else if (contributors == null) {
            return "";
        } else {
            arr = [String(contributors).trim()];
        }

        if (arr.length === 0) {
            return "";
        }

        return arr[0].toLowerCase();
    };

    const startsWithDate = (value: string): boolean => {
        return /^\d{2}\/\d{2}/.test(value);
    };

    const getNumericSortValue = (value: any): number => {
        if (value == null) {
            return 0;
        }

        if (Array.isArray(value)) {
            value = value.length > 0 ? value[0] : "";
        }

        if (typeof value === 'object') {
            value = value.value ?? value;
        }

        const normalized = String(value)
            .trim()
            .replace(/[^0-9.\-]+/g, "");

        const numeric = Number(normalized);
        return Number.isFinite(numeric) ? numeric : 0;
    };

    const numericSortSections = ['ipiNumber', 'ipNumber', 'ipNameNumber', 'perfShare', 'mechShare'];

    const getContributorSortKey = (value: string): string => {
        const v = (value ?? "").toString().trim();

        return buildContributorSortKey(v);
    };

    React.useEffect(() => {
        if (!searchResults) {
            setCurrentTableData([]);
            return;
        }

        let tableContentsCopy = createCopyOfTableContents(searchResults);

        const workStatusMappings = portalWorkStatusConfiguration?.mappings ?? [];
        if (workStatusMappings.length > 0) {
            tableContentsCopy = tableContentsCopy.map(result => {
                if (!result.status || result.status === "-") {
                    return result;
                }

                const workStatusCode = lookupValues?.[WORK_STATUS_LOOKUP]?.lookups?.find(lookup => lookup.description === result.status)?.code ?? result.status;
                const mapping = workStatusMappings.find(mapping => mapping.backOfficeStatusCodes.includes(workStatusCode));

                return mapping ? { ...result, status: mapping.portalDisplayName } : result;
            });
        }

        if (activeSortHeaderSection) {
            tableContentsCopy.sort((a: any, b: any) => {
                let newA = a[activeSortHeaderSection] ?? "";
                let newB = b[activeSortHeaderSection] ?? "";

                if (activeSortHeaderSection === 'dateLoaded') {
                    newA = newA.split('/').reverse().join();
                    newB = newB.split('/').reverse().join();
                }

                let normA: string;
                let normB: string;

                if (activeSortHeaderSection === 'contributors') {
                    normA = getContributorSortKey(newA);
                    normB = getContributorSortKey(newB);
                } else {
                    normA = (newA ?? "").toString().trim();
                    normB = (newB ?? "").toString().trim();
                }

                const aHasDate = startsWithDate(normA);
                const bHasDate = startsWithDate(normB);

                if (aHasDate !== bHasDate) {
                    return isAscending
                        ? (aHasDate ? -1 : 1)
                        : (aHasDate ? 1 : -1);
                }

                if (numericSortSections.includes(activeSortHeaderSection)) {
                    const valueA = getNumericSortValue(a[activeSortHeaderSection]);
                    const valueB = getNumericSortValue(b[activeSortHeaderSection]);

                    return isAscending
                        ? valueA - valueB
                        : valueB - valueA;
                }

                if (isAscending) {
                    return normA.localeCompare(normB, undefined, {
                        sensitivity: 'base'
                    });
                }
                return normB.localeCompare(normA, undefined, {
                    sensitivity: 'base'
                });
            });
        }

        const currentResults = tableContentsCopy.slice(
            indexOfFirstResult,
            indexOfLastResult
        );
        setCurrentTableData(currentResults);
    }, [searchResults, indexOfFirstResult, indexOfLastResult, activeSortHeaderSection, isAscending, lookupValues, portalWorkStatusConfiguration]);

    const updateContentsTable = (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => {
        updatePagination(indexOfFirstResult, indexOfLastResult, resultsPerPage, currentPage, repertoireSection);
    }

    const sortTableByColumn = (section: string, currentAscending?: boolean) => {
        const nextAscending = typeof currentAscending === 'boolean'
            ? !currentAscending
            : activeSortHeaderSection === section
                ? !isAscending
                : false;

        let tableContentsCopy = createCopyOfTableContents(searchResults);

        tableContentsCopy.sort((a: any, b: any) => {
            let newA = a[section] ?? "";
            let newB = b[section] ?? "";

            if (section === 'dateLoaded') {
                newA = newA.split('/').reverse().join();
                newB = newB.split('/').reverse().join();
            }

            let normA: string;
            let normB: string;

            if (section === 'contributors') {
                normA = getContributorSortKey(newA);
                normB = getContributorSortKey(newB);
            } else {
                normA = (newA ?? "").toString().trim();
                normB = (newB ?? "").toString().trim();
            }


            const aHasDate = startsWithDate(normA);
            const bHasDate = startsWithDate(normB);

            if (aHasDate !== bHasDate) {
                return nextAscending
                    ? (aHasDate ? -1 : 1)
                    : (aHasDate ? 1 : -1);
            }

            if (numericSortSections.includes(section)) {
                const valueA = getNumericSortValue(a[section]);
                const valueB = getNumericSortValue(b[section]);

                return nextAscending
                    ? valueA - valueB
                    : valueB - valueA;
            }

            if (nextAscending) {
                return normA.localeCompare(normB, undefined, {
                    sensitivity: 'base'
                });
            }
            return normB.localeCompare(normA, undefined, {
                sensitivity: 'base'
            });
        });

        const currentResults = tableContentsCopy.slice(
            indexOfFirstResult,
            indexOfLastResult
        );
        setCurrentTableData(currentResults);
        setActiveHeaderSection(section);
        setIsAscending(nextAscending);

        if (sortSearchResults) {
            sortSearchResults(tableContentsCopy);
        }
    };

    if ((searchResultsTableData && searchResultsTableData.length > 0) && (searchResults && searchResults.length > 0)) {
        const paginationData = searchResultsTableData.filter(field => field.componentInstance === PAGINATION);
        const searchResultsData = searchResultsTableData.filter(field => field.componentInstance !== PAGINATION && field.componentInstance !== MODAL_VIEW);

        return <div className="row">
            <div className="col-md-12 searchResultsDiv">
                <div className="SearchResultsTable">
                    <PaginationView
                        key='pagination1'
                        contentsLength={searchResults ? searchResults.length : 0}
                        resultsPerPage={resultsPerPage}
                        currentPage={currentPage}
                        updateContentsTable={updateContentsTable}
                        paginationData={paginationData}
                        repertoireSection={repertoireSection}
                    />
                    <DisplayTable
                        tableContents={currentTableData}
                        dataGridTableData={searchResultsData}
                        isSearchResultTable={true}
                        sortTableByColumn={sortTableByColumn}
                        activeSortHeaderSection={activeSortHeaderSection}
                        isAscending={isAscending}
                        rowActions={rowActions}
                        isExpandable={true}
                        getAgreementDetails={getAgreementDetails}
                        addToPackages={addToPackages}
                        showYesNoPrompt={showYesNoPrompt}
                        hideModal={hideModal}
                        packageActionsDisabled={packageActionsDisabled}
                        packageMessageData={packageMessageData}
                    />
                    <PaginationView
                        key='pagination2'
                        contentsLength={searchResults ? searchResults.length : 0}
                        resultsPerPage={resultsPerPage}
                        currentPage={currentPage}
                        updateContentsTable={updateContentsTable}
                        paginationData={paginationData}
                        repertoireSection={repertoireSection}
                    />
                </div>
            </div>
        </div>
    }

    if (searchSuccessful !== undefined && (searchResults && searchResults.length === 0)) {
        return (
            <div className="row">
                <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 centerTextNotFound" >
                    {searchResultsTableData.find((v, i, a) => v.name == "noSearchResultsMessage")?.data || 'No results found.'}
                </div>
            </div>
        )
    }

    return null;
}
export default DisplayTableSearchResults